import type { BlockField } from '@/blocks/domain/types';
import type { HeaderContent, HeaderSection, SectionLayout } from '@/blocks/domain/types/BlockContent/HeaderContent';

/**
 * THE SECTION MODEL (design ruling 2026-08-27).
 *
 *   - A block is a stack of SECTIONS. A section owns exactly one thing — its layout
 *     (`SectionLayout`); elements live in its column slots.
 *   - A single-column section holding at most one element has no visual existence — no label, no
 *     outline, no row in any list. Adding a lone element silently makes one. A section earns
 *     visibility the moment it has layout (2+ columns) or groups several things.
 *   - Sections never nest — block → section → column → element, one level, full stop.
 *
 * This file is the one place the model is derived from data, shared by the editor and the
 * document renderer so the panel and the page can never disagree about which section a field
 * sits in.
 */

/** The five layouts in the design's order (Figma `be.panel.add`): 1, 2, 3 columns, then 1:2 before 2:1. */
export const SECTION_LAYOUTS: SectionLayout[] = ['1', '2', '3', '1:2', '2:1'];

/** How many column slots a layout provides. */
export function sectionColumnCount(layout: SectionLayout): number {
  if (layout === '2' || layout === '2:1' || layout === '1:2') return 2;
  if (layout === '3') return 3;
  return 1;
}

/** A section with layout '1' and at most one element is implicit — invisible chrome. */
export function isImplicitSection(section: HeaderSection, fieldsInSection: number): boolean {
  return section.layout === '1' && fieldsInSection <= 1;
}

export interface NormalizedHeader {
  sections: HeaderSection[];
  /** All fields with sectionId + column filled in, in global sortOrder. */
  fields: BlockField[];
}

/**
 * Bring any header content — including everything saved before sections existed — to the section
 * model. Legacy derivation mirrors what the old flat shape meant on screen: with a `columns` count
 * of 2+, non-heading fields are distributed across that many columns of one section; `columns`
 * absent, `0` or `1` puts every field in its own implicit (invisible) one-column section, in order.
 * Idempotent for already-normalized content.
 *
 * IDs are deterministic (derived from the field's own id, or a fixed literal for the legacy
 * multi-column section) — normalization can run on every render, and a random id would remount
 * the page's section subtrees mid-edit.
 */
export function normalizeHeaderSections(content: HeaderContent): NormalizedHeader {
  const sorted = [...content.fields].sort((a, b) => a.sortOrder - b.sortOrder);

  if (content.sections && content.sections.length > 0) {
    const known = new Set(content.sections.map((s) => s.id));
    const sections = [...content.sections];
    const fields = sorted.map((f) => {
      if (f.sectionId && known.has(f.sectionId)) return f;
      // A field with no (or a stale) section home gets its own implicit one, in place.
      const sec: HeaderSection = { id: `sec-of-${f.id}`, layout: '1' };
      sections.push(sec);
      return { ...f, sectionId: sec.id, column: 0 };
    });
    return { sections: orderSections(sections, fields), fields };
  }

  // ---- legacy shapes (no `sections` yet) ----
  const columnCount = content.columns ?? 0;
  if (columnCount < 2) {
    // Every field is its own implicit one-column section, in order.
    const sections: HeaderSection[] = [];
    const fields = sorted.map((f) => {
      const sec: HeaderSection = { id: `sec-of-${f.id}`, layout: '1' };
      sections.push(sec);
      return { ...f, sectionId: sec.id, column: 0 };
    });
    return { sections, fields };
  }

  const layout: SectionLayout = columnCount >= 3 ? '3' : '2';
  const mainSection: HeaderSection = { id: 'sec-legacy-columns', layout };
  const slots = sectionColumnCount(layout);
  const per = Math.ceil(sorted.length / slots) || 1;

  const fields = sorted.map((f, idx) => {
    const derived = Math.min(typeof f.column === 'number' ? f.column : Math.floor(idx / per), slots - 1);
    return { ...f, sectionId: mainSection.id, column: derived };
  });

  return { sections: sorted.length > 0 ? [mainSection] : [], fields };
}

/**
 * Sections in the order their first field appears. A section with NO field has no first field, so
 * it keeps its stored place relative to the sections around it (it follows the section that
 * precedes it in storage) — an empty section the author dropped between two others stays there
 * instead of sinking to the end on the next load.
 */
function orderSections(sections: HeaderSection[], fields: BlockField[]): HeaderSection[] {
  const firstIndex = new Map<string, number>();
  fields.forEach((f, i) => {
    if (f.sectionId && !firstIndex.has(f.sectionId)) firstIndex.set(f.sectionId, i);
  });
  const filled = sections.filter((s) => firstIndex.has(s.id)).sort((a, b) => (firstIndex.get(a.id) ?? 0) - (firstIndex.get(b.id) ?? 0));

  const emptiesAfter = new Map<string | null, HeaderSection[]>();
  let anchor: string | null = null;
  for (const s of sections) {
    if (firstIndex.has(s.id)) {
      anchor = s.id;
      continue;
    }
    emptiesAfter.set(anchor, [...(emptiesAfter.get(anchor) ?? []), s]);
  }
  const ordered: HeaderSection[] = [...(emptiesAfter.get(null) ?? [])];
  for (const s of filled) ordered.push(s, ...(emptiesAfter.get(s.id) ?? []));
  return ordered;
}

/** The fields of one section, one array per column slot, in sortOrder. */
export function sectionColumns(section: HeaderSection, fields: BlockField[]): BlockField[][] {
  const slots = sectionColumnCount(section.layout);
  const cols: BlockField[][] = Array.from({ length: slots }, () => []);
  for (const f of fields) {
    if (f.sectionId !== section.id) continue;
    const ci = Math.min(typeof f.column === 'number' ? f.column : 0, slots - 1);
    cols[ci]?.push(f);
  }
  return cols;
}

/**
 * Canonical order after any structural edit: fields flattened section-by-section (slots in order,
 * existing in-slot order kept) and `sortOrder` rewritten. Every editor mutation that changes
 * section/column membership funnels through this so the panel, the canvas and the persisted
 * content can never disagree.
 */
export function canonicalizeHeader(sections: HeaderSection[], fields: BlockField[]): NormalizedHeader {
  const bySection = new Map<string, BlockField[][]>();
  for (const sec of sections)
    bySection.set(
      sec.id,
      Array.from({ length: sectionColumnCount(sec.layout) }, () => [])
    );

  for (const f of fields) {
    const slots = bySection.get(f.sectionId ?? '');
    if (!slots) continue;
    const ci = Math.min(typeof f.column === 'number' ? f.column : 0, slots.length - 1);
    slots[ci]?.push(f);
  }

  const flat: BlockField[] = [];
  for (const sec of sections) {
    const slots = bySection.get(sec.id) ?? [];
    slots.forEach((col, ci) => {
      for (const f of col) flat.push({ ...f, sectionId: sec.id, column: ci });
    });
  }

  return { sections: [...sections], fields: flat.map((f, idx) => ({ ...f, sortOrder: idx })) };
}

/** True when at least one section is visible (layout other than a lone single-column element). */
export function headerHasVisibleSection(content: HeaderContent): boolean {
  const normalized = normalizeHeaderSections(content);
  return normalized.sections.some((section) => {
    const count = normalized.fields.filter((field) => field.sectionId === section.id).length;
    return !isImplicitSection(section, count);
  });
}

/**
 * Replace one column's fields and rewrite global order through `canonicalizeHeader`.
 * Other columns keep their relative order.
 */
export function applyColumnFields(
  content: HeaderContent,
  sectionId: string,
  columnIndex: number,
  nextColumnFields: BlockField[]
): HeaderContent {
  const normalized = normalizeHeaderSections(content);
  const stamped = nextColumnFields.map((field) => ({ ...field, sectionId, column: columnIndex }));
  const others = normalized.fields.filter((field) => field.sectionId !== sectionId || field.column !== columnIndex);
  const canon = canonicalizeHeader(normalized.sections, [...others, ...stamped]);
  return { ...content, sections: canon.sections, fields: canon.fields };
}

/**
 * Reorder a run of fields (a flat letterhead, or the implicit one-field sections that sit beside a
 * real section) without disturbing every other field's place. Section order follows the new field
 * order so the layers list and the canvas stay in step.
 */
export function applyFieldRunOrder(content: HeaderContent, orderedFields: BlockField[]): HeaderContent {
  const normalized = normalizeHeaderSections(content);
  const orderedIds = new Set(orderedFields.map((field) => field.id));
  const queue = [...orderedFields];
  const nextFields = normalized.fields.map((field) => {
    if (!orderedIds.has(field.id)) return field;
    return queue.shift() ?? field;
  });
  const canon = canonicalizeHeader(orderSections(normalized.sections, nextFields), nextFields);
  return { ...content, sections: canon.sections, fields: canon.fields };
}

/**
 * Put a newly added element into the latest real section (the one the author just chose in the
 * Add panel), in the column that currently holds the fewest fields. A flat letterhead — every
 * field still in its own implicit section — keeps the new field on the flat list.
 */
export function appendElementToHeader(content: HeaderContent, field: BlockField): HeaderContent {
  const normalized = normalizeHeaderSections(content);
  const target = [...normalized.sections].reverse().find((section) => {
    const count = normalized.fields.filter((item) => item.sectionId === section.id).length;
    return !isImplicitSection(section, count);
  });
  if (!target) {
    return { ...content, fields: [...content.fields, { ...field, sortOrder: content.fields.length }] };
  }

  const columns = sectionColumns(target, normalized.fields);
  let columnIndex = 0;
  let lightest = columns[0]?.length ?? 0;
  columns.forEach((column, index) => {
    if (column.length < lightest) {
      lightest = column.length;
      columnIndex = index;
    }
  });
  return applyColumnFields(content, target.id, columnIndex, [...(columns[columnIndex] ?? []), field]);
}
